export declare namespace DeficiencyStepTableTokenSections {
	interface PreviousConfirmRow {
		background: string;
		editBackground: string;
		borderColor: string;
		titleColor: string;
		deadlineDashColor: string;
	}

	interface PreviousDeficienciesTable {
		theadBackground: string;
		theadColor: string;
		theadBorderColor: string;
		thPadding: string;
		confirmControlMinHeight: string;
		confirmControlFontSize: string;
		confirmControlLineHeight: string;
		confirmControlPaddingX: string;
		confirmControlPaddingY: string;
		disabledFieldBackground: string;
		confirmTitleFontSize: string;
		confirmTitleLineHeight: string;
		statusBadgeFontSize: string;
		statusBadgeLineHeight: string;
		statusBadgeFontWeight: string;
		statusBadgePadding: string;
		statusBadgeMinHeight: string;
		statusImplementedBackground: string;
		statusImplementedColor: string;
		statusNotImplementedBackground: string;
		statusNotImplementedColor: string;
		statusPartialBackground: string;
		statusPartialColor: string;
		statusUnconfirmedBackground: string;
		statusUnconfirmedColor: string;
	}

	interface PreviousAccordionTitle {
		fontSize: string;
		lineHeight: string;
		segmentGap: string;
		registrationColor: string;
		nameColor: string;
		separatorColor: string;
	}

	interface ActivitySelect {
		selectedTextColor: string;
		optionTextColor: string;
	}

	interface AccordionGroup {
		sectionGap: string;
		sectionsMarginBottom: string;
		contentGap: string;
		subtitleFontSize: string;
		subtitleLineHeight: string;
		subtitleFontWeight: string;
		subtitleColor: string;
		previousGroupedAccordionsGap: string;
	}

	interface Card {
		padding: string;
		borderColor: string;
		borderRadius: string;
		background: string;
	}

	interface EmptyCard {
		padding: string;
	}

	interface AccordionHeader {
		background: string;
		color: string;
		fontSize: string;
		fontWeight: string;
		lineHeight: string;
		padding: string;
		borderRadius: string;
	}

	interface AccordionContent {
		padding: string;
	}

	interface AdditionalDeficienciesTable {
		sectionActionsJustifyContent: string;
		sectionActionsGap: string;
		colWidths: {
			createdDate: string;
			activities: string;
			deficiencies: string;
			directions: string;
			deadline: string;
		};
	}

	interface DetectedAutoTable {
		accordionToTableGap: string;
		confirmActionsMarginTop: string;
		confirmActionsJustifyContent: string;
		confirmActionsGap: string;
		theadBackground: string;
		theadColor: string;
		theadBorderColor: string;
		thPadding: string;
		readonlyFieldBackground: string;
		colCreatedDate: string;
		colActivities: string;
		colDeadline: string;
		controlMinHeight: string;
	}
}

export type DeficiencyStepTableTokens = {
	previousConfirmRow: DeficiencyStepTableTokenSections.PreviousConfirmRow;
	previousDeficienciesTable: DeficiencyStepTableTokenSections.PreviousDeficienciesTable;
	previousAccordionTitle: DeficiencyStepTableTokenSections.PreviousAccordionTitle;
	activitySelect: DeficiencyStepTableTokenSections.ActivitySelect;
	pageTitleGap: string;
	accordionGroup: DeficiencyStepTableTokenSections.AccordionGroup;
	card: DeficiencyStepTableTokenSections.Card;
	emptyCard: DeficiencyStepTableTokenSections.EmptyCard;
	accordionHeader: DeficiencyStepTableTokenSections.AccordionHeader;
	accordionContent: DeficiencyStepTableTokenSections.AccordionContent;
	additionalDeficienciesTable: DeficiencyStepTableTokenSections.AdditionalDeficienciesTable;
	detectedAutoTable: DeficiencyStepTableTokenSections.DetectedAutoTable;
};

export const previousConfirmRow: DeficiencyStepTableTokenSections.PreviousConfirmRow = {
	/** Figma 11880:85174 — confirm sub-row (view mode) */
	background: '{surface.50}',
	/** Figma 11780:47689 — confirm sub-row (edit mode) */
	editBackground: '{surface.100}',
	borderColor: '{surface.200}',
	titleColor: '{text.muted.color}',
	deadlineDashColor: '{primary.color}',
};

/** Figma 11880:85174 — ankstesnių trūkumų OKIS_tables (scoped CSS overrides). */
export const previousDeficienciesTable: DeficiencyStepTableTokenSections.PreviousDeficienciesTable = {
	theadBackground: '{primary.50}',
	theadColor: '{primary.600}',
	theadBorderColor: '{primary.100}',
	thPadding: '8px',
	confirmControlMinHeight: '32px',
	confirmControlFontSize: '12px',
	confirmControlLineHeight: '18px',
	confirmControlPaddingX: '12px',
	confirmControlPaddingY: '7px',
	disabledFieldBackground: '{surface.100}',
	confirmTitleFontSize: '14px',
	confirmTitleLineHeight: '24px',
	statusBadgeFontSize: '12px',
	statusBadgeLineHeight: '20px',
	statusBadgeFontWeight: '500',
	statusBadgePadding: '4px 8px',
	statusBadgeMinHeight: '28px',
	statusImplementedBackground: '{green.100}',
	statusImplementedColor: '{green.600}',
	statusNotImplementedBackground: '{red.50}',
	statusNotImplementedColor: '{red.600}',
	statusPartialBackground: '{yellow.100}',
	statusPartialColor: '{yellow.950}',
	statusUnconfirmedBackground: '{surface.100}',
	statusUnconfirmedColor: '{surface.500}',
};

export const previousAccordionTitle: DeficiencyStepTableTokenSections.PreviousAccordionTitle = {
	fontSize: '16px',
	lineHeight: '26px',
	segmentGap: '16px',
	registrationColor: '{text.muted.color}',
	nameColor: '{primary.contrast.color}',
	separatorColor: '{text.muted.color}',
};

export const activitySelect: DeficiencyStepTableTokenSections.ActivitySelect = {
	selectedTextColor: '{surface.950}',
	optionTextColor: '{surface.950}',
};

export const accordionGroup: DeficiencyStepTableTokenSections.AccordionGroup = {
	sectionGap: '16px',
	sectionsMarginBottom: '16px',
	contentGap: '16px',
	subtitleFontSize: '18px',
	subtitleLineHeight: '30px',
	subtitleFontWeight: '500',
	subtitleColor: '{text.color}',
	/** Vertical space between each previous-deficiency activity group accordion (Figma). */
	previousGroupedAccordionsGap: '31px',
};

export const card: DeficiencyStepTableTokenSections.Card = {
	padding: '16px',
	/** Figma card border: surface.100 (#E4E7EC) — intentionally lighter than content.border.color (surface.200). */
	borderColor: '{surface.100}',
	borderRadius: '{content.border.radius}',
	background: '{content.background}',
};

export const emptyCard: DeficiencyStepTableTokenSections.EmptyCard = {
	padding: '32px',
};

export const accordionHeader: DeficiencyStepTableTokenSections.AccordionHeader = {
	background: '{surface.700}',
	color: '{primary.contrast.color}',
	fontSize: '18px',
	fontWeight: '500',
	lineHeight: '30px',
	padding: '8px 16px',
	borderRadius: '{border.radius.sm}',
};

export const accordionContent: DeficiencyStepTableTokenSections.AccordionContent = {
	padding: '16px',
};

/** Figma — „Papildomi trūkumai" (scoped CSS overrides). */
export const additionalDeficienciesTable: DeficiencyStepTableTokenSections.AdditionalDeficienciesTable = {
	sectionActionsJustifyContent: 'flex-start',
	sectionActionsGap: '16px',
	/** Figma OKIS_tables additional thead column widths (px, used with `table-layout: fixed`). */
	colWidths: {
		createdDate: '148px',
		activities: '256px',
		deficiencies: '349.5px',
		directions: '349.5px',
		deadline: '204px',
	},
};

/** Figma 11832:28288 — „Automatiškai nustatyti trūkumai" (scoped CSS overrides). */
export const detectedAutoTable: DeficiencyStepTableTokenSections.DetectedAutoTable = {
	accordionToTableGap: '24px',
	confirmActionsMarginTop: '48px',
	confirmActionsJustifyContent: 'flex-start',
	confirmActionsGap: '16px',
	theadBackground: '{primary.50}',
	theadColor: '{primary.600}',
	theadBorderColor: '{primary.100}',
	thPadding: '8px',
	readonlyFieldBackground: '{surface.100}',
	colCreatedDate: '148px',
	colActivities: '256px',
	colDeadline: '204px',
	controlMinHeight: '48px',
};

/**
 * Deficiency-step–specific tokens (tags, DTO review).
 * Shared table chrome lives under `inspectionTables` (Figma OKIS_tables).
 */
export default {
	previousConfirmRow,
	previousDeficienciesTable,
	previousAccordionTitle,
	activitySelect,
	/** Figma 11832:26998 — 40px nuo title iki turinio (title margin-bottom 24px + šis 16px). */
	pageTitleGap: '16px',
	accordionGroup,
	card,
	emptyCard,
	accordionHeader,
	accordionContent,
	additionalDeficienciesTable,
	detectedAutoTable,
} satisfies DeficiencyStepTableTokens;
